import styled from "styled-components";
import { Fragment, useEffect } from "react";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { sendAnalyticsNonInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import Info from "src/design-system/icons/Info.svg?react";
import {
  fontSize,
  fontWeight,
  spacing,
  textColor,
  lineHeight,
  notificationColor,
} from "../../../theme";
import useSearch from "../../../utils/hooks/useSearch";

type NotificationLink = {
  url: string;
  text: string;
  linkType?: string;
};

type Notification = {
  level: number;
  message: string;
  generic?: boolean;
  links?: NotificationLink[];
};

export function SearchNotification(props: Notification) {
  const color = getNotificationColor(props.level);

  // Log that we showed a notification but only log once per search.
  const { searchResponse } = useSearch();
  const requestId = searchResponse?.request.requestId;

  useEffect(() => {
    if (requestId) {
      sendAnalyticsNonInteractionEvent({
        category: "SearchNotification",
        action: "Shown",
        label: `${props.level}|${props.message}`,
      });
    }
  }, [requestId, props.level, props.message]);

  function handleClick(url: string) {
    sendAnalyticsInteractionEvent({
      category: "SearchNotification",
      action: "Click:Link",
      label: `${props.level}|${url}`,
    });
  }

  return (
    <Root bg={color} data-testid="search-notification">
      <Info className="mt-1 h-5 w-4 text-icon-primary"></Info>
      <Text>
        {props.message}
        {props.links?.map((link, index) => {
          // Add space before a link and separate links with a bar.
          const separator = index === 0 ? " " : " | ";
          return (
            <Fragment key={index}>
              {separator}
              <Link
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() => handleClick(link.url)}
              >
                {link.text}
              </Link>
            </Fragment>
          );
        })}
      </Text>
    </Root>
  );
}

function getNotificationColor(level: number) {
  const colors = [
    notificationColor.low,
    notificationColor.medium,
    notificationColor.high,
  ];
  return colors[level] ?? "inherit";
}

const Root = styled.div<{ bg?: string }>`
  display: inline-block;
  background-color: ${(props) => props.bg};
  padding: ${spacing.md} ${spacing.lg};
  line-height: ${lineHeight.normal};
  font-size: ${fontSize.h6};
  width: 100%;
  display: flex;
`;

const Text = styled.div`
  padding-left: ${spacing.md};
`;

const Link = styled.a`
  color: ${textColor.primaryOnLight};
  font-weight: ${fontWeight.medium};
`;
